import React, { useMemo } from 'react'
import { IonItemDivider, IonLabel, IonSpinner, IonText } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useDeviceMetrics from '../../../../../utilities/useDeviceMetrics'
import { getQaAddendumSelector } from '../../selectors'
import { IQaChartSummaryPage } from '../../store/qaAddendum.types'
import ChartSummaryElements from './ChartSummaryElements.content'
import ChartSummaryList from './ChartSummaryList.content'
import './ChartSummaryPageList.content.css'
import {
  buildChartSummaryPageList,
  getChartSummaryPageSection,
  getPageDescriptionKey,
  hasRenderableContent,
} from '../../../utils/qaAddendumChartSummary.util'

interface IChartSummaryPageListContentProps {
  isLoading?: boolean
  onPageClick?: (page: IQaChartSummaryPage) => void
}

const PageType = [
  { section: 'dispatch', labelKey: 'fullChart.Dispatch' },
  { section: 'patient', labelKey: 'fullChart.Patient' },
]

const ChartSummaryPageListContent: React.FC<IChartSummaryPageListContentProps> = ({
  isLoading = false,
  onPageClick,
}) => {
  const { t } = useTranslation()
  const { chartSummary } = useSelector(getQaAddendumSelector)
  const { isTablet: isTabletDevice } = useDeviceMetrics()

  const { rootElements, pages, isEmpty } = useMemo(() => buildChartSummaryPageList(chartSummary), [chartSummary])

  const items = useMemo(
    () =>
      pages.map(({ key, page }) => {
        const descriptionKey = getPageDescriptionKey(page.pageName)
        return {
          key,
          section: getChartSummaryPageSection(page.pageName),
          title: page.pageName,
          subtitle: descriptionKey ? t(descriptionKey) : page.description,
          testId: `QaChartSummaryPage-${key}`,
        }
      }),
    [pages, t]
  )

  if (isEmpty) {
    return isLoading ? (
      <IonSpinner className='qa-addendum-loader' data-testid='QaChartSummarySpinner' />
    ) : (
      <IonText className='qa-addendum-chart-summary-empty ion-padding' data-testid='QaChartSummaryEmpty'>
        {t('qaFlags.qaAddendum.flagResolution.chartSummaryEmpty')}
      </IonText>
    )
  }

  const handleSelect = (key: string) => {
    const selected = pages.find((entry) => entry.key === key)
    if (selected) onPageClick?.(selected.page)
  }

  return (
    <div className='qa-addendum-chart-summary-list' data-testid='QaChartSummaryList'>
      {hasRenderableContent(rootElements) && (
        <div className='qa-addendum-chart-summary-root-card' data-testid='QaChartSummaryRootCard'>
          <ChartSummaryElements elements={rootElements} keyPrefix='chart-summary-root' showOptionIds />
        </div>
      )}
      <div className='qa-addendum-chart-summary-sections'>
        {PageType.map(({ section, labelKey }) => {
          const columnItems = items.filter((item) => item.section === section)
          return columnItems.length ? (
            <div
              key={section}
              className='qa-addendum-chart-summary-section'
              data-testid={`QaChartSummarySection-${section}`}>
              <IonItemDivider
                color='light'
                className={`qa-addendum-chart-summary-section-header ${
                  isTabletDevice
                    ? 'qa-addendum-chart-summary-section-header-plain'
                    : 'qa-addendum-chart-summary-section-header-band'
                }`}>
                <IonLabel className='qa-addendum-chart-summary-section-title'>{t(labelKey)}</IonLabel>
              </IonItemDivider>
              <ChartSummaryList items={columnItems} onSelect={handleSelect} />
            </div>
          ) : null
        })}
      </div>
    </div>
  )
}

export default ChartSummaryPageListContent
